import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

declare global {
  // eslint-disable-next-line no-var
  var __supabaseClient__: SupabaseClient | undefined;
}

// Custom lock with a 10s timeout cap so cross-tab lock contention can't freeze the page
const customLock = async <R>(name: string, acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
  const lockManager = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!lockManager) return fn();
  const cap = Math.min(acquireTimeout || 10_000, 10_000);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), cap);
  try {
    return await lockManager.request(name, { signal: controller.signal }, async () => fn());
  } catch {
    return await fn();
  } finally {
    clearTimeout(timer);
  }
};

export const supabase: SupabaseClient =
  globalThis.__supabaseClient__ ??
  createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      lock: customLock,
    },
  });

if (!globalThis.__supabaseClient__) {
  globalThis.__supabaseClient__ = supabase;
}

export type ProductMedia = { url: string; type: string };

export type DbCategory = {
  id: number;
  name: string;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

export type DbProduct = {
  id: number;
  name: string;
  category_id: number | null;
  status: string;
  description: string | null;
  pricing_mode: number;
  currency: string;
  price: number | null;
  stock: number | null;
  discount_enabled: boolean;
  discount_price: number | null;
  media: ProductMedia[] | null;
  created_at?: string;
  updated_at?: string;
  product_categories?: DbCategory | null;
};

export const getProductImage = (p: Pick<DbProduct, "media">) =>
  p.media && p.media.length > 0 ? p.media[0].url : "";

export const getProductFinalPrice = (p: Pick<DbProduct, "price" | "discount_enabled" | "discount_price">) =>
  p.discount_enabled && p.discount_price != null ? Number(p.discount_price) : Number(p.price ?? 0);